import { computed, onUnmounted, ref, getCurrentInstance } from "@vue/composition-api";
import { CollabLiveChat, CollabLiveChatStatus } from "@scrile/api-provider/dist/api/CollabLiveChatsProvider";
import { LivechatSubjectType, LiveChatUser } from "@scrile/api-provider/dist/api/LivechatsProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import useOnUserLogin from "@/hooks/useOnUserLogin";
import providers from "@/lib/api";
import { ROLES } from "@/const";
import useLiveChat from "../../hooks/useLiveChat";

export default function useCollabLiveChat({
  chatId,
  userId,
  referrerId,
}: {
  chatId: string;
  userId: string;
  referrerId?: string;
}) {
  const vm = getCurrentInstance();

  let collabUpdateUnsubscribe = () => {};

  const subject = ref({
    subjectId: chatId,
    subjectType: LivechatSubjectType.COLLAB_LIVE,
  });

  const {
    loading,
    joinData,
    clientServerTimeDiff,
    isBlockedForSession,
    errorData,
    setOnUserBroadcastingCB,
    joinChat,
    endChat,
  } = useLiveChat({ subject, referrerId: computed(() => referrerId) });

  useOnUserLogin(async () => {
    await joinChat();
  });

  const userList = computed<LiveChatUser[]>(() =>
    (joinData.value?.users ?? []).filter(i => i.userId !== userId && i.user?.role !== ROLES.ADMIN)
  );

  const referrersMap = computed(() =>
    joinData.value?.users.reduce((acc, user) => {
      if (user.referrer) {
        acc[user.userId] = user.referrer;
      }
      return acc;
    }, {} as Record<string, UserPublicData>)
  );

  const usersAmountByReferrer = computed(() => {
    if (!referrersMap.value) return {};
    return Object.values(referrersMap.value).reduce((acc, referrer) => {
      acc[referrer.id] = acc[referrer.id] ? acc[referrer.id] + 1 : 1;
      return acc;
    }, {} as Record<string, number>);
  });

  const endEntireCollabLive = async () => {
    await providers.CollabLiveChatsProvider.update({
      data: {
        id: chatId,
        status: CollabLiveChatStatus.ENDED,
      },
    });
  };

  const onCollabUpdatedCB = (collabLive: CollabLiveChat) => {
    if (!vm) return;
    if (collabLive.status === CollabLiveChatStatus.ENDED) {
      return vm.$emit("close");
    }
    vm.$emit("collabUpdated", collabLive);
  };

  const subscribeCollabUpdated = async () => {
    const subscription = await providers.CollabLiveChatsProvider.subscribeCollabUpdated(
      chatId,
      (data: CollabLiveChat) => {
        onCollabUpdatedCB(data);
      }
    );
    collabUpdateUnsubscribe = () => subscription.unsubscribe();
  };

  joinChat();
  subscribeCollabUpdated();

  onUnmounted(() => {
    collabUpdateUnsubscribe();
  });

  return {
    loading,
    joinData,
    userList,
    clientServerTimeDiff,
    errorData,
    referrersMap,
    usersAmountByReferrer,
    isBlockedForSession,
    setOnUserBroadcastingCB,
    endChat,
    endEntireCollabLive,
  };
}
